import React from 'react';
import { Box, TextField, Button, Typography, CircularProgress, Select, MenuItem, FormControl, InputLabel, Divider, Autocomplete, Chip, Alert, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, IconButton, Tooltip } from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { uploadCsvPreview, generateTableDescription } from '../../services/api';

interface AddNewTableProps {
  aiId: string;
  tableName: string;
  onTableNameChange: (value: string) => void;
  tableDescription: string;
  onTableDescriptionChange: (value: string) => void;
  apiUrl: string;
  onApiUrlChange: (value: string) => void;
  apiKey: string;
  onApiKeyChange: (value: string) => void;
  apiSecret: string;
  onApiSecretChange: (value: string) => void;
  rowLimit: number;
  onRowLimitChange: (value: number) => void;
  addingTable: boolean;
  fetchingSchema: boolean;
  onFetchSchema: (selectedTables: string[]) => void;
  onAddTable: () => void;
  onCsvUpload: (csvData: any, fileName: string) => void;
  tableSchema: any;
}

// Define the available database sources and their common table types
const DATABASE_SOURCES = {
  WooCommerce: {
    label: 'WooCommerce',
    commonTables: ['Products', 'Orders', 'Customers', 'Coupons', 'Subscriptions', 'Refunds', 'Payment_gateways', 'Tax_rates', 'Shipping_methods'],
    supportsCustomTables: true,
    variationSupportedTables: ['Products'] // Currently only Products support variations, but detection will be added later
  },
  MongoDB: {
    label: 'MongoDB',
    commonTables: ['Collections'],
    supportsCustomTables: true,
    variationSupportedTables: [] // No variation support for MongoDB yet
  },
  'CSV Upload': {
    label: 'CSV Upload',
    commonTables: [],
    supportsCustomTables: false,
    variationSupportedTables: []
  }
};

const AddNewTable: React.FC<AddNewTableProps> = ({
  aiId,
  tableName,
  onTableNameChange,
  tableDescription,
  onTableDescriptionChange,
  apiUrl,
  onApiUrlChange,
  apiKey,
  onApiKeyChange,
  apiSecret,
  onApiSecretChange,
  rowLimit,
  onRowLimitChange,
  addingTable,
  fetchingSchema,
  onFetchSchema,
  onCsvUpload,
  tableSchema
}) => {
  const [selectedSource, setSelectedSource] = React.useState<keyof typeof DATABASE_SOURCES | ''>('');
  const [selectedTableTypes, setSelectedTableTypes] = React.useState<string[]>([]);
  const [customTableName, setCustomTableName] = React.useState<string>('');
  const [useCustomTable, setUseCustomTable] = React.useState<boolean>(false);
  
  // CSV Upload states
  const [csvFile, setCsvFile] = React.useState<File | null>(null);
  const [csvPreviewData, setCsvPreviewData] = React.useState<any>(null);
  const [uploadingCsv, setUploadingCsv] = React.useState<boolean>(false);
  
  // AI Description Generation states
  const [generatingDescription, setGeneratingDescription] = React.useState<boolean>(false);

  // Handle CSV file upload and preview
  const handleCsvFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    setCsvFile(file);
    setUploadingCsv(true);
    setCsvPreviewData(null);
    
    try {
      const response = await uploadCsvPreview(aiId, file);
      console.log('CSV Upload Response:', response);
      console.log('Response Data:', response.data);
      
      // Extract the preview data from the nested structure
      const previewData = response.data.preview;
      console.log('Preview Data:', previewData);
      console.log('Columns:', previewData?.columns);
      console.log('Preview Rows:', previewData?.previewRows);
      console.log('Complete Sample Data:', previewData?.sampleData?.length, 'rows');
      
      // Debug: Check data format consistency using backend metadata
      if (previewData?.columns && previewData?.previewRows?.length > 0) {
        const firstRow = previewData.previewRows[0];
        const columnNames = previewData.columns.map((col: any) => col.name);
        const originalNames = previewData.columns.map((col: any) => col.originalName);
        const rowKeys = Object.keys(firstRow);
        
        console.log('🔍 Data Format Analysis:');
        console.log('  Column names (cleaned):', columnNames);
        console.log('  Original names:', originalNames);
        console.log('  First row keys:', rowKeys);
        console.log('  Uses cleaned names:', columnNames.every((name: string) => rowKeys.includes(name)));
        console.log('  Uses original names:', originalNames.every((name: string) => rowKeys.includes(name)));
        
        // Use backend validation metadata if available
        if (previewData.dataFormat) {
          console.log('📊 Backend validation results:');
          console.log('  Uses cleaned names (backend):', previewData.dataFormat.usesCleanedNames);
          console.log('  Uses original names (backend):', previewData.dataFormat.usesOriginalNames);
          console.log('  Data keys (backend):', previewData.dataFormat.dataKeys);
          console.log('  Possible mismatches:', previewData.dataFormat.possibleMismatches);
          
          if (previewData.dataFormat.possibleMismatches?.length > 0) {
            console.warn('⚠️ Backend detected data access issues:', previewData.dataFormat.possibleMismatches);
          }
        }
        
        // Check for potential data access issues
        const missingColumns = columnNames.filter((name: string) => !rowKeys.includes(name) && !originalNames.includes(name));
        if (missingColumns.length > 0) {
          console.warn('⚠️ Frontend detected data access issues - missing columns:', missingColumns);
        }
      }
      
      // Keep sampleData as is (contains all rows), previewRows is just for display
      // Don't overwrite sampleData with previewRows - sampleData contains the complete dataset!
      
      setCsvPreviewData(previewData);
    } catch (error: any) {
      console.error('Error previewing CSV:', error);
      console.error('Error details:', error.response?.data || error.message);
      
      let errorMessage = 'Unknown error occurred while processing CSV file.';
      
      if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      } else if (error.response?.status === 413) {
        errorMessage = 'File too large. Please upload a CSV file smaller than 10MB.';
      } else if (error.response?.status === 415) {
        errorMessage = 'Invalid file type. Please upload a valid CSV file.';
      } else if (error.response?.status >= 500) {
        errorMessage = 'Server error while processing CSV. Please try again later.';
      } else if (!navigator.onLine) {
        errorMessage = 'No internet connection. Please check your connection and try again.';
      }
      
      // Show user-friendly error message
      alert(`Error processing CSV file: ${errorMessage}\n\nTroubleshooting tips:\n• Ensure the file is a valid CSV format\n• Check that the file size is under 10MB\n• Verify the CSV has proper headers and data\n• Try re-saving the file as CSV if it was converted from Excel`);
      
      // Reset CSV state on error
      setCsvFile(null);
      setCsvPreviewData(null);
    } finally {
      setUploadingCsv(false);
    }
  };

  // Handle AI description generation for CSV tables
  const handleGenerateDescription = async () => {
    if (!csvPreviewData || !csvPreviewData.columns) {
      alert('Please upload and preview a CSV file first.');
      return;
    }

    setGeneratingDescription(true);
    
    try {
      console.log('🤖 Generating AI description for CSV data...');
      
      // Call the backend AI service with CSV data
      // Use a temporary tableId since the table doesn't exist yet
      const response = await generateTableDescription(aiId, 'temp', csvPreviewData);
      
      const generatedDescription = response.data.description;
      
      if (generatedDescription && generatedDescription.trim()) {
        onTableDescriptionChange(generatedDescription);
        console.log('✅ AI description generated successfully');
      } else {
        throw new Error('Generated description is empty');
      }
      
    } catch (error: any) {
      console.error('Error generating CSV description:', error);
      console.error('Error details:', error.response?.data || error.message);
      
      let errorMessage = 'Failed to generate AI description. Please try again or enter a description manually.';
      
      if (error.response?.data?.error) {
        errorMessage = `AI Generation Error: ${error.response.data.error}`;
      } else if (error.response?.status === 500) {
        errorMessage = 'AI service temporarily unavailable. Please try again in a moment.';
      } else if (!navigator.onLine) {
        errorMessage = 'No internet connection. Please check your connection and try again.';
      }
      
      alert(errorMessage);
    } finally {
      setGeneratingDescription(false);
    }
  };


  // Update URL when source changes
  React.useEffect(() => {
    if (selectedSource === 'WooCommerce') {
      onApiUrlChange('https://aixue.us/wp-json/wc/v3');
    } else if (selectedSource === 'MongoDB') {
      onApiUrlChange('mongodb://localhost:27017');
    } else if (selectedSource === 'CSV Upload') {
      // Clear API fields for CSV upload
      onApiUrlChange('');
      // Reset CSV-related state when switching to CSV
      setCsvFile(null);
      setCsvPreviewData(null);
    }
  }, [selectedSource, onApiUrlChange]);

  return (
    <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
      {/* Database Source Selection */}
      <FormControl fullWidth>
        <InputLabel>Database Source</InputLabel>
        <Select
          value={selectedSource}
          label="Database Source"
          onChange={(e) => {
            setSelectedSource(e.target.value as keyof typeof DATABASE_SOURCES);
            setSelectedTableTypes([]); // Reset table types when source changes
            setCustomTableName(''); // Reset custom table name
            setUseCustomTable(false); // Reset custom table toggle
          }}
          disabled={addingTable || fetchingSchema}
        >
          {Object.entries(DATABASE_SOURCES).map(([key, value]) => (
            <MenuItem key={key} value={key}>{value.label}</MenuItem>
          ))}
        </Select>
      </FormControl>

      {/* CSV Upload Section */}
      {selectedSource === 'CSV Upload' && (
        <>
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              Upload CSV File
            </Typography>
            <Divider />
          </Box>
          
          <Button
            variant="outlined"
            component="label"
            disabled={uploadingCsv || addingTable}
            sx={{ alignSelf: 'flex-start' }}
          >
            Choose CSV File
            <input
              type="file"
              hidden
              accept=".csv"
              onChange={handleCsvFileChange}
            />
          </Button>
          
          {csvFile && (
            <Alert severity="info">
              Selected file: <strong>{csvFile.name}</strong> ({Math.round(csvFile.size / 1024)} KB)
            </Alert>
          )}
          
          {uploadingCsv && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={20} />
              <Typography>Processing CSV file...</Typography>
            </Box>
          )}
          
          {csvPreviewData && (
            <>
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  CSV Preview ({csvPreviewData?.totalRows || 0} rows, {csvPreviewData?.columns?.length || 0} columns)
                </Typography>
                
                {/* Show error message if columns are missing */}
                {csvPreviewData && (!csvPreviewData.columns || csvPreviewData.columns.length === 0) && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    CSV processing incomplete. No columns detected in the file. Please ensure:
                    <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
                      <li>The file has a header row with column names</li>
                      <li>The file is properly formatted CSV</li>
                      <li>The file is not corrupted</li>
                    </ul>
                  </Alert>
                )}
                
                {/* Show error message if preview data is missing but columns exist */}
                {csvPreviewData?.columns && csvPreviewData.columns.length > 0 && (!csvPreviewData.previewRows || csvPreviewData.previewRows.length === 0) && (
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    CSV columns detected ({csvPreviewData.columns.length} columns) but no data rows found. 
                    This could mean:
                    <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
                      <li>The file only contains headers</li>
                      <li>All data rows are empty</li>
                      <li>There was an issue processing the data</li>
                    </ul>
                  </Alert>
                )}
                
                {/* Show success message for valid CSV */}
                {csvPreviewData?.columns && csvPreviewData.columns.length > 0 && csvPreviewData?.previewRows && csvPreviewData.previewRows.length > 0 && (
                  <Alert severity="success" sx={{ mb: 2 }}>
                    CSV file processed successfully! Found {csvPreviewData.columns.length} columns and {csvPreviewData.totalRows} data rows.
                  </Alert>
                )}
                
                {csvPreviewData?.previewRows && csvPreviewData.previewRows.length > 0 && csvPreviewData?.columns && (
                  <TableContainer component={Paper} sx={{ maxHeight: 300, mb: 2 }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          {csvPreviewData.columns.map((col: any, index: number) => (
                            <TableCell key={index}>
                              <Box>
                                <Typography variant="body2" fontWeight="bold">
                                  {col.name}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {col.type}
                                </Typography>
                              </Box>
                            </TableCell>
                          ))}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {csvPreviewData.previewRows.slice(0, 5).map((row: any, rowIndex: number) => (
                          <TableRow key={rowIndex}>
                            {csvPreviewData.columns.map((col: any, colIndex: number) => {
                              // Try both cleaned name and original name for data access
                              const cellValue = row[col.name] !== undefined ? row[col.name] : row[col.originalName];
                              const displayValue = String(cellValue || '');
                              
                              return (
                                <TableCell key={colIndex}>
                                  <Typography variant="body2">
                                    {displayValue.substring(0, 100)}
                                    {displayValue.length > 100 ? '...' : ''}
                                  </Typography>
                                </TableCell>
                              );
                            })}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
                
                <Box sx={{ position: 'relative' }}>
                  <TextField
                    label="Table Description"
                    value={tableDescription}
                    onChange={(e) => onTableDescriptionChange(e.target.value)}
                    disabled={addingTable}
                    fullWidth
                    multiline
                    rows={3}
                    placeholder="Describe what this CSV data contains..."
                    InputProps={{
                      endAdornment: (
                        <Tooltip title="Generate description with AI">
                          <IconButton
                            onClick={handleGenerateDescription}
                            disabled={!csvPreviewData?.columns || generatingDescription || addingTable}
                            sx={{ 
                              position: 'absolute',
                              top: 8,
                              right: 8,
                              bgcolor: 'primary.main',
                              color: 'white',
                              '&:hover': { bgcolor: 'primary.dark' },
                              '&:disabled': { bgcolor: 'grey.300', color: 'grey.500' }
                            }}
                            size="small"
                          >
                            {generatingDescription ? (
                              <CircularProgress size={16} color="inherit" />
                            ) : (
                              <AutoAwesomeIcon fontSize="small" />
                            )}
                          </IconButton>
                        </Tooltip>
                      )
                    }}
                  />
                </Box>
                
                <Button
                  variant="contained"
                  onClick={() => {
                    if (csvPreviewData && csvFile && csvPreviewData.columns && csvPreviewData.sampleData) {
                      onCsvUpload(csvPreviewData, csvFile.name);
                    }
                  }}
                  disabled={!csvPreviewData || !csvFile || !csvPreviewData?.columns || !csvPreviewData?.sampleData || addingTable}
                  sx={{ mt: 2 }}
                >
                  {addingTable ? <CircularProgress size={20} /> : 'Create Table from CSV'}
                </Button>
              </Box>
            </>
          )}
        </>
      )}

      {/* Table Type Selection - only show if a non-CSV source is selected */}
      {selectedSource && selectedSource !== 'CSV Upload' && (
        <>
          {/* Toggle between common tables and custom table name */}
          <FormControl fullWidth>
            <Button
              variant={useCustomTable ? "outlined" : "contained"}
              onClick={() => {
                setUseCustomTable(!useCustomTable);
                setSelectedTableTypes([]);
                setCustomTableName('');
              }}
              disabled={addingTable || fetchingSchema}
            >
              {useCustomTable ? 'Use Common Tables' : 'Enter Custom Table Name'}
            </Button>
          </FormControl>

          {!useCustomTable ? (
            <Autocomplete
              multiple
              value={selectedTableTypes}
              onChange={(event, newValue) => {
                setSelectedTableTypes(newValue);
              }}
              options={DATABASE_SOURCES[selectedSource].commonTables}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Common Table Types"
                  placeholder="Select tables to import"
                  helperText={selectedSource && DATABASE_SOURCES[selectedSource as keyof typeof DATABASE_SOURCES]?.variationSupportedTables.length > 0 ? 
                    `Tables with variation support: ${DATABASE_SOURCES[selectedSource as keyof typeof DATABASE_SOURCES].variationSupportedTables.join(', ')}` : 
                    'No tables support variations for this source'}
                />
              )}
              renderTags={(value: readonly string[], getTagProps) =>
                value.map((option: string, index: number) => {
                  const sourceConfig = selectedSource ? DATABASE_SOURCES[selectedSource as keyof typeof DATABASE_SOURCES] : null;
                  const supportsVariations = sourceConfig?.variationSupportedTables?.includes(option as any) || false;
                  return (
                    <Chip
                      variant="outlined"
                      label={`${option}${supportsVariations ? ' ⚡' : ''}`}
                      {...getTagProps({ index })}
                      disabled={addingTable || fetchingSchema}
                      color={supportsVariations ? 'primary' : 'default'}
                    />
                  );
                })
              }
              disabled={addingTable || fetchingSchema}
            />
          ) : (
            <TextField
              label="Custom Table Name"
              value={customTableName}
              onChange={(e) => setCustomTableName(e.target.value)}
              disabled={addingTable || fetchingSchema}
              fullWidth
              placeholder="Enter any WooCommerce endpoint (e.g., products, orders, customers, coupons, subscriptions, etc.)"
              helperText={`The system will check if this table exists in WooCommerce and import it${selectedSource && DATABASE_SOURCES[selectedSource].variationSupportedTables.length > 0 ? '. Variations will be automatically detected for supported tables (' + DATABASE_SOURCES[selectedSource].variationSupportedTables.join(', ').toLowerCase() + ').' : '.'}`}
            />
          )}
        </>
      )}

      {/* Show configuration fields if a non-CSV source is selected */}
      {selectedSource && selectedSource !== 'CSV Upload' && (
        <>
          <Box sx={{ mt: 3, mb: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              {selectedSource} Configuration
            </Typography>
            <Divider />
          </Box>

          <TextField
            label="API URL"
            value={apiUrl}
            onChange={(e) => onApiUrlChange(e.target.value)}
            disabled={addingTable || fetchingSchema}
            fullWidth
          />

          <TextField
            label="API Key"
            value={apiKey}
            onChange={(e) => onApiKeyChange(e.target.value)}
            disabled={addingTable || fetchingSchema}
            fullWidth
          />

          <TextField
            label="API Secret"
            value={apiSecret}
            onChange={(e) => onApiSecretChange(e.target.value)}
            disabled={addingTable || fetchingSchema}
            fullWidth
            type="password"
          />

          <TextField
            label="Description"
            value={tableDescription}
            onChange={(e) => onTableDescriptionChange(e.target.value)}
            disabled={addingTable || fetchingSchema}
            fullWidth
            multiline
            rows={3}
          />

          <TextField
            label="Row Limit for Sync"
            type="number"
            value={rowLimit}
            onChange={(e) => onRowLimitChange(parseInt(e.target.value, 10) || 1000)}
            disabled={addingTable || fetchingSchema}
            fullWidth
            inputProps={{ min: 1, max: 10000 }}
            helperText="Maximum number of rows to sync from WooCommerce. Note: Data sync happens in the background after database creation."
          />

          <Alert severity="info" sx={{ mt: 2 }}>
            <Typography variant="body2">
              <strong>Process:</strong> First, the database schema will be created (this is quick). 
              Then data will be synced in the background, which may take several minutes for large datasets.
              You can check sync progress by refreshing the table list or manually syncing the table.
            </Typography>
          </Alert>

          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button 
              variant="contained" 
              onClick={() => {
                console.log('=== DEBUG: Button clicked ===');
                console.log('useCustomTable:', useCustomTable);
                console.log('customTableName:', customTableName);
                console.log('selectedTableTypes:', selectedTableTypes);
                console.log('selectedSource:', selectedSource);
                
                const tablesToImport = useCustomTable ? [customTableName] : selectedTableTypes;
                // Filter out empty table names and log for debugging
                const validTables = tablesToImport.filter(name => name && name.trim());
                console.log('Tables to import (raw):', tablesToImport);
                console.log('Valid tables after filtering:', validTables);
                console.log('=== END DEBUG ===');
                
                if (validTables.length > 0) {
                  onFetchSchema(validTables);
                } else {
                  console.error('No valid table names to import');
                  alert('Please select a table type or enter a custom table name');
                }
              }}
              disabled={
                fetchingSchema || 
                addingTable || 
                (useCustomTable ? !customTableName.trim() : selectedTableTypes.length === 0)
              }
            >
              {fetchingSchema ? <CircularProgress size={20} /> : 'Create Database & Start Sync'}
            </Button>
          </Box>

          {tableSchema && Object.keys(tableSchema).length > 0 && (
            <Box>
              <Typography variant="subtitle2">Available Tables:</Typography>
              <Box
                component="pre"
                sx={{
                  maxHeight: '200px',
                  overflow: 'auto',
                  p: 1,
                  bgcolor: 'background.paper',
                  border: '1px solid',
                  borderColor: 'divider'
                }}
              >
                {JSON.stringify(tableSchema, null, 2)}
              </Box>
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

export default AddNewTable;
